import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { fetchResultadosSevac } from "../utils/contentfulRequests";
import { fetchGlobalConfig } from "../utils/contentfulRequests";
import {
  Box,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  List,
  useTheme,
  Paper,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";

const Sevac = ({ tipo = "Sevac Entry", titulo = "Sistema de Evaluaciones de la Armonización Contable", mostrarLogo = true }) => {
  const theme = useTheme();
  const [resultados, setResultados] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [globalConfig, setGlobalConfig] = useState(null);

  const [expanded, setExpanded] = useState(false);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    fetchResultadosSevac(tipo).then((data) => {
      setResultados(data);
    });
    fetchGlobalConfig().then(setGlobalConfig);
  }, [tipo]);

  useEffect(() => {
    setExpanded(false);
  }, [tabIndex]);

  // Agrupa por año
  const agrupados = resultados.reduce((acc, res) => {
    acc[res.ano] = acc[res.ano] || [];
    acc[res.ano].push(res);
    return acc;
  }, {});

  const years = Object.keys(agrupados)
    .map(Number)
    .sort((a, b) => b - a);

  return (
    <Box
      sx={{
        maxWidth: '100%',
        mx: "auto",
        minHeight: '70vh',
        py: { xs: 2, md: 3, xl: 4 },
        px: { xs: 1, sm: 2 },
        background: theme.palette.secondary.main,
      }}
    >
      {/* Logo SEvac */}
      {mostrarLogo && globalConfig?.logoSevac && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img
            src={globalConfig.logoSevac}
            alt="Logo SEvac"
            style={{ height: 64, maxWidth: "100%", objectFit: "contain" }}
          />
        </Box>
      )}
      <Typography variant="h4" align="center" gutterBottom>
        {titulo}
      </Typography>
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label={`Años ${titulo}`}
          centered={years.length < 4}
          TabIndicatorProps={{
            style: {
              backgroundColor: theme.palette.highlight.main,
              height: 4,
              borderRadius: 2,
            },
          }}
        >
          {years.map((ano) => (
            <Tab key={ano} label={`Año ${ano}`} />
          ))}
        </Tabs>
      </Paper>
      {years.length === 0 ? (
        <Typography align="center" color="text.secondary">
          No hay resultados disponibles.
        </Typography>
      ) : (
        agrupados[years[tabIndex]] &&
        agrupados[years[tabIndex]]
          .sort((a, b) => a.trimestre - b.trimestre)
          .map((res) => (
            <Accordion
  key={res.trimestre}
  expanded={expanded === res.trimestre}
  onChange={handleAccordionChange(res.trimestre)}
  sx={{
    mb: 2,
    boxShadow: expanded === res.trimestre ? 4 : 1,
    borderLeft: expanded === res.trimestre
      ? `6px solid ${theme.palette.highlight.main}`
      : `2px solid ${theme.palette.divider}`,
    backgroundColor: expanded === res.trimestre
      ? theme.palette.background.component
      : theme.palette.background.default,
    transition: "background 0.3s, border-color 0.3s, box-shadow 0.3s",
  }}
>
  <AccordionSummary
    expandIcon={<ExpandMoreIcon />}
    aria-controls={`panel-${res.trimestre}-content`}
    id={`panel-${res.trimestre}-header`}
    sx={{
      "& .MuiTypography-root": {
        fontWeight: expanded === res.trimestre ? 700 : 500,
        color: expanded === res.trimestre
          ? theme.palette.primary.main
          : theme.palette.text.primary,
        transition: "color 0.3s, font-weight 0.3s",
      },
    }}
  >
    <Typography variant="h6">Trimestre {res.trimestre}</Typography>
  </AccordionSummary>
  <AccordionDetails>
  {res.documentos && res.documentos.length > 0 ? (
    <List>
     <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
  {res.documentos.map((doc, idx) => (
    <Box
      key={doc.nombre + idx}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        boxShadow: 1,
        transition: "background 0.3s, color 0.3s",
        cursor: doc.pdf ? "pointer" : "default",
        "&:hover": {
          backgroundColor: theme.palette.primary.main,
          color: "#fff",
          "& .download-btn": {
            opacity: 1,
            transform: "translateX(0)",
          },
          "& a": {
            fontWeight:'700',
            color: "#fff",
          },
        },
      }}
      onClick={() => {
        if (doc.pdf) window.open(doc.pdf, "_blank", "noopener,noreferrer");
      }}
    >
      <Typography
        variant="body1"
        sx={{
          fontWeight: 700,
          flex: 1,
          color: "inherit",
          transition: "color 0.3s",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textDecoration: "none",
        }}
        component={doc.pdf ? "a" : "span"}
        href={doc.pdf || undefined}
        target={doc.pdf ? "_blank" : undefined}
        rel={doc.pdf ? "noopener noreferrer" : undefined}
        onClick={e => {
          // Evita doble navegación si se hace click en el link
          e.stopPropagation();
        }}
      >
        {doc.nombre}
      </Typography>
      {doc.pdf && (
        <Box
          className="download-btn"
          sx={{
            ml: 2,
            opacity: 0,
            transform: "translateX(16px)",
            transition: "opacity 0.3s, transform 0.3s",
            display: "flex",
            alignItems: "center",
          }}
        >
          <DownloadIcon sx={{ fontSize: 28 }} />
        </Box>
      )}
    </Box>
  ))}
</Box>
    </List>
  ) : (
    <Typography color="text.secondary">
      Sin documentos para este trimestre.
    </Typography>
  )}
</AccordionDetails>
</Accordion>
          ))
      )}
    </Box>
  );
};

export default Sevac;

Sevac.propTypes = {
  tipo: PropTypes.string,
  titulo: PropTypes.string,
  mostrarLogo: PropTypes.bool,
};
